import { Directionality } from "@angular/cdk/bidi";
import { CdkConnectedOverlay } from "@angular/cdk/overlay";
import { Platform } from "@angular/cdk/platform";
import { DOCUMENT } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Host,
  Inject,
  Input,
  Optional,
  Output,
  QueryList,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewChildren,
  forwardRef,
} from "@angular/core";
import { NG_VALUE_ACCESSOR } from "@angular/forms";
import { NzResizeObserver } from "ng-zorro-antd/cdk/resize-observer";
import { NzConfigService, WithConfig } from "ng-zorro-antd/core/config";
import { NzNoAnimationDirective } from "ng-zorro-antd/core/no-animation";
import { FunctionProp, NzSafeAny } from "ng-zorro-antd/core/types";
import { InputBoolean } from "ng-zorro-antd/core/util";
import { NzDatePickerComponent } from "ng-zorro-antd/date-picker";
import { DateHelperService, NzDatePickerI18nInterface, NzI18nService } from "ng-zorro-antd/i18n";
import { UiSizeLDSType } from "@webapp/ui/core/models/ui-size.models";
import { UiCompatibleDate, UiDateMode, UiDisabledTimeFn, UiPresetRanges, UiSupportTimeOptions } from "@webapp/ui/date-picker/date-picker.models";
import { UiDateRangePopupComponent } from "./components/date-range-popup/date-range-popup.component";
import { UiDatePickerService } from "./services/date-picker.service";

const POPUP_STYLE_PATCH = { position: "relative" }; // Aim to override antd's style to support overlay's position strategy (position:absolute will cause it not working because the overlay can't get the height/width of it's content)

/**
 * The base picker for all common APIs
 */
@Component({
  selector: "ui-date-picker,ui-week-picker,ui-month-picker,ui-year-picker,ui-range-picker",
  exportAs: "uiDatePicker",
  templateUrl: "date-picker.component.html",
  providers: [
    UiDatePickerService,
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => UiDatePickerComponent),
    },
  ],
})
export class UiDatePickerComponent extends NzDatePickerComponent {
  // --- Common API
  @Input("uiAllowClear")
  @InputBoolean()
  public nzAllowClear = true;
  @Input("uiAutoFocus")
  @InputBoolean()
  public nzAutoFocus = false;
  @Input("uiDisabled")
  @InputBoolean()
  public nzDisabled = false;
  @Input("uiBorderless")
  @InputBoolean()
  public nzBorderless = false;
  @Input("uiInputReadOnly")
  @InputBoolean()
  public nzInputReadOnly = false;
  @Input("uiInline")
  @InputBoolean()
  public nzInline = false;
  @Input("uiOpen")
  @InputBoolean()
  public nzOpen?: boolean;
  @Input("uiDisabledDate")
  public nzDisabledDate?: (d: Date) => boolean;
  @Input("uiLocale")
  public nzLocale!: NzDatePickerI18nInterface;
  @Input("uiPlaceHolder")
  public nzPlaceHolder: string | string[] = "";
  @Input("uiPopupStyle")
  public nzPopupStyle: object = POPUP_STYLE_PATCH;
  @Input("uiDropdownClassName")
  public nzDropdownClassName?: string;
  @Input("uiSize")
  public nzSize: UiSizeLDSType = "default";
  @Input("uiFormat")
  public nzFormat!: string;
  @Input("uiDateRender")
  public nzDateRender?: TemplateRef<NzSafeAny> | string | FunctionProp<TemplateRef<Date> | string>;
  @Input("uiDisabledTime")
  public nzDisabledTime?: UiDisabledTimeFn;
  @Input("uiRenderExtraFooter")
  public nzRenderExtraFooter?: TemplateRef<NzSafeAny> | string | FunctionProp<TemplateRef<NzSafeAny> | string>;
  @Input("uiShowToday")
  @InputBoolean()
  public nzShowToday = true;
  @Input("uiMode")
  public nzMode: UiDateMode = "date";
  @Input("uiShowNow")
  @InputBoolean()
  public nzShowNow = true;
  @Input("uiRanges")
  public nzRanges?: UiPresetRanges;
  @Input("uiDefaultPickerValue")
  public nzDefaultPickerValue: UiCompatibleDate | null = null;
  @Input("uiSeparator")
  @WithConfig()
  public nzSeparator?: string = undefined;
  @Input("uiSuffixIcon")
  @WithConfig()
  public nzSuffixIcon: string | TemplateRef<NzSafeAny> = "calendar";
  @Input("uiBackdrop")
  @WithConfig()
  public nzBackdrop = false;
  @Input("uiId")
  public nzId: string | null = null;
  @Input() get uiShowTime(): UiSupportTimeOptions | boolean {
    return this.nzShowTime;
  }

  set uiShowTime(value: UiSupportTimeOptions | boolean) {
    this.nzShowTime = value;
  }

  @Output("uiOnPanelChange")
  public readonly nzOnPanelChange = new EventEmitter<UiDateMode | UiDateMode[] | string | string[]>();
  @Output("uiOnCalendarChange")
  public readonly nzOnCalendarChange = new EventEmitter<Array<Date | null>>();
  @Output("uiOnOk")
  public readonly nzOnOk = new EventEmitter<UiCompatibleDate | null>();
  @Output("uiOnOpenChange")
  public readonly nzOnOpenChange = new EventEmitter<boolean>();

  // ------------------------------------------------------------------------
  // Input API Start
  // ------------------------------------------------------------------------
  @ViewChild(CdkConnectedOverlay, { static: false })
  public cdkConnectedOverlay?: CdkConnectedOverlay;
  @ViewChild(UiDateRangePopupComponent, { static: false })
  public panel!: UiDateRangePopupComponent;
  @ViewChild("separatorElement", { static: false })
  public separatorElement?: ElementRef;
  @ViewChild("pickerInput", { static: false })
  public pickerInput?: ElementRef<HTMLInputElement>;
  @ViewChildren("rangePickerInput")
  public rangePickerInputs?: QueryList<ElementRef<HTMLInputElement>>;

  // ------------------------------------------------------------------------
  // Input API End
  // ------------------------------------------------------------------------

  constructor(
    public uiConfigService: NzConfigService,
    public datePickerService: UiDatePickerService,
    protected i18n: NzI18nService,
    protected cdr: ChangeDetectorRef,
    renderer: Renderer2,
    elementRef: ElementRef,
    dateHelper: DateHelperService,
    uiResizeObserver: NzResizeObserver,
    platform: Platform,
    @Inject(DOCUMENT) doc: NzSafeAny,
    @Optional() directionality: Directionality,
    @Host() @Optional() noAnimation?: NzNoAnimationDirective
  ) {
    super(uiConfigService, datePickerService, i18n, cdr, renderer, elementRef, dateHelper, uiResizeObserver, platform, doc, directionality, noAnimation);
  }
}
